import { CommonModule } from '@angular/common';
import { Component, EventEmitter, inject, input, Output } from '@angular/core';
import { NovaIcon } from '@rds-nova/angular-icon';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

import { HighlightDataDirective } from 'src/app/features/help-faq/directives/highlight-data.directive';
import { FaqAdapter } from 'src/app/features/help-faq/services/faq.adapter';

export type SigningKind = 'Ron' | 'IPEN' | 'Hybrid';

export interface ChatBubble {
  kind: 'bot' | 'user';
  text: string;
  textKey?: string;
  params?: Record<string, unknown>;
}

export interface ChatTyping {
  kind: 'typing';
}

export type ChatItem = ChatBubble | ChatTyping;

export interface ChatChip {
  id: string;
  label: string;
  targetNodeId?: string;
  parentNodeId?: string;
}

type ChipsDirection = 'row' | 'column';

@Component({
  selector: 'app-chat-window',
  standalone: true,
  imports: [CommonModule, NovaIcon,
    TranslatePipe,
    HighlightDataDirective,
  ],
  templateUrl: './chat-window.component.html',
  styleUrl: './chat-window.component.scss',
})
export class ChatWindowComponent {

  readonly chipsDirection = input<ChipsDirection>('column');

  readonly headerTitle = input('help-faq.CHAT_HEADER_TITLE');
  readonly privacyLinkHref = input('#');
  readonly items = input<ChatItem[]>([]);
  readonly chips = input<ChatChip[]>([]);
  readonly rootChips = input<ChatChip[]>([]);
  readonly selectedRootChipId = input('');
  readonly chipsDisabled = input(false);

  showMissing = true;

  private readonly faq = inject(FaqAdapter);
  private readonly translate = inject(TranslateService);

  protected readonly disbursementDate = this.faq.formattedDisbursementDate;
  protected readonly firstPaymentDate = this.faq.formattedFirstPaymentDate;
  protected readonly interestRate = this.faq.formattedInterestRate;
  protected readonly monthlyPayment = this.faq.formatedMonthlyPayment;
  protected readonly prepaymentPenalty = this.faq.formattedPrepaymentPenalty;
  protected readonly borrowerNames = this.faq.borrowerNames;
  protected readonly participantNames = this.faq.participantNames;
  protected readonly participantsLoading = this.faq.participantsLoading;
  protected readonly hasData = this.faq.hasData;
  protected readonly payoffs = this.faq.payoffs;
  protected readonly cashToCloseFromLoanEstimate = this.faq.cashToCloseFromLoanEstimate;
  protected readonly cashToCloseFinalIsFromBorrower = this.faq.cashToCloseFinalIsFromBorrower;
  protected readonly displayFundingTransfer = this.faq.displayFundingTransfer;
  protected readonly hasNbthParticipants = this.faq.hasNbthParticipants;

  @Output() chipClick = new EventEmitter<ChatChip>();
  @Output() sendMessage = new EventEmitter<string>();
  @Output() minimize = new EventEmitter<void>();
  @Output() closed = new EventEmitter<void>();

  protected resolveChipLabel(chip: ChatChip) {
    if (chip.targetNodeId === undefined) {
      return '';
    }

    return `help-faq.categories.${chip.targetNodeId}`;
  }

  protected onChipClick(chip: ChatChip) {
    this.chipClick.emit(chip);
  }

  protected isBubble(item: ChatItem): item is ChatBubble {
    return item.kind === 'bot' || item.kind === 'user';
  }

  protected isTyping(item: ChatItem): item is ChatTyping {
    return item.kind === 'typing';
  }

  protected translateBubble(item: ChatBubble): string {
    if (!item.textKey) {
      return item.text;
    }

    const params = item.params
      ? Object.fromEntries(
        Object.entries(item.params).map(([key, value]) =>
          typeof value === 'string' && value.startsWith('help-faq.')
            ? [key, this.translate.instant(value)]
            : [key, value],
        ),
      )
      : undefined;

    return this.translate.instant(item.textKey, params);
  }
}
